import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageTitle, Btn, ScoreBadge } from "./ui";
import { analyzeSeo } from "../lib/seo";
import { parseSchema } from "../lib/head";

const liveScore = (p) => analyzeSeo({ title: p.title, metaTitle: p.seo?.meta_title, metaDesc: p.seo?.meta_description, slug: p.slug, html: p.content_html,
  keyword: p.seo?.focus_keyword, image: p.featured_image || p.seo?.og_image, schema: p.seo?.schema_type !== "None" || parseSchema(p.seo?.schema_json).length > 0 }).score;

export default function PostsList({ kind }) {
  const [items, setItems] = useState(null);
  const label = kind === "blog" ? "Blogs" : "Pages";
  const load = () => api.get(`/admin/posts?kind=${kind}`).then(({ data }) => setItems(data));
  useEffect(() => { setItems(null); load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/posts/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={`admin-${kind}-list`}>
      <PageTitle title={label} sub={kind === "blog" ? "Write articles in HTML or generate them with AI" : "Custom pages such as About, Terms or Contact"}>
        <Link to={`/admin/${kind === "blog" ? "blogs" : "pages"}/new`} className="btn-gold !py-2" data-testid={`new-${kind}`}><Plus size={15} /> New {kind}</Link>
      </PageTitle>
      {items === null ? <p className="text-zinc-500">Loading…</p> : items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-[#D4AF37]/25 p-10 text-center text-sm text-zinc-500" data-testid={`${kind}-empty`}>Nothing here yet — create your first {kind}.</p>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="flex flex-col gap-4 rounded-2xl border border-[#D4AF37]/20 bg-[#101117] p-4 sm:flex-row sm:items-center" data-testid={`${kind}-row-${p.slug}`}>
              <ScoreBadge score={liveScore(p)} testid={`${kind}-score-${p.slug}`} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-white">{p.title}</p>
                <p className="mt-0.5 truncate text-xs text-zinc-500">/{kind === "blog" ? "blog" : "p"}/{p.slug} · {p.seo?.focus_keyword ? `keyword: ${p.seo.focus_keyword}` : "no focus keyword"}</p>
              </div>
              <span className={`w-fit rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${p.status === "published" ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-700/40 text-zinc-400"}`}>{p.status}</span>
              <div className="flex gap-1">
                {p.status === "published" && <a href={`/${kind === "blog" ? "blog" : "p"}/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-zinc-500 hover:text-white" aria-label="View"><ExternalLink size={16} /></a>}
                <Link to={`/admin/${kind === "blog" ? "blogs" : "pages"}/${p.id}`} className="p-2 text-zinc-500 hover:text-[#D4AF37]" data-testid={`edit-${p.slug}`} aria-label="Edit"><Pencil size={16} /></Link>
                <button onClick={() => remove(p)} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`delete-${p.slug}`} aria-label="Delete"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
